import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const SmtpSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    host: '',
    port: 587,
    secure: true,
    auth: {
      user: '',
      pass: ''
    },
    from: '',
    isConfigured: false,
    allowInsecureTLS: false,
    imap: {
      host: '',
      port: 993,
      autoCreateLeads: true,
      isConfigured: false,
      lastSyncAt: null,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadSmtpSettings();
  }, []);

  const loadSmtpSettings = async () => {
    try {
      const response = await api.get('/smtp/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading SMTP settings:', error);
      setMessage('An error occurred while loading SMTP settings');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setSettings(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await api.put('/smtp/settings', settings);
      setMessage('SMTP settings updated successfully!');
      setMessageType('success');
      setSettings(prev => ({ ...prev, isConfigured: true }));
    } catch (error) {
      console.error('Error updating SMTP settings:', error);
      setMessage(error.response?.data?.error || 'An error occurred while updating SMTP settings');
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage('');

    try {
      // Mevcut ayarları kullanarak test et (şifre gizli olduğu için boş body gönder)
      const response = await api.post('/smtp/test', {});
      setMessage('SMTP test successful! Test email sent.');
      setMessageType('success');
    } catch (error) {
      console.error('SMTP test error:', error);
      setMessage(error.response?.data?.error || 'SMTP test failed');
      setMessageType('error');
    } finally {
      setTesting(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset SMTP settings?')) {
      return;
    }

    try {
      await api.delete('/smtp/settings');
      setSettings({
        host: '',
        port: 587,
        secure: true,
        auth: {
          user: '',
          pass: ''
        },
        from: '',
        isConfigured: false
      });
      setMessage('SMTP settings reset');
      setMessageType('success');
    } catch (error) {
      console.error('Error resetting SMTP settings:', error);
      setMessage('Error occurred while resetting SMTP settings');
      setMessageType('error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading SMTP settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">SMTP Settings</h1>
            <p className="mt-1 text-sm text-gray-600">
              Configure email sending settings for your organization
            </p>
          </div>

          {/* Message */}
          {message && (
            <div className={`mx-6 mt-4 p-4 rounded-md ${
              messageType === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* SMTP Host */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMTP Host *
                </label>
                <input
                  type="text"
                  name="host"
                  value={settings.host}
                  onChange={handleInputChange}
                  placeholder="smtp.gmail.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Port */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Port *
                </label>
                <select
                  name="port"
                  value={settings.port}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select port</option>
                  <option value="587">587 (TLS - Recommended)</option>
                  <option value="465">465 (SSL)</option>
                  <option value="25">25 (SMTP)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Port 587: TLS (recommended) | Port 465: SSL | Port 25: SMTP
                </p>
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email / Username *
                </label>
                <input
                  type="email"
                  name="auth.user"
                  value={settings.auth.user}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password / App Password *
                </label>
                <input
                  type="password"
                  name="auth.pass"
                  value={settings.auth.pass}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* From Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Email Address *
                </label>
                <input
                  type="email"
                  name="from"
                  value={settings.from}
                  onChange={handleInputChange}
                  placeholder="noreply@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!settings.allowInsecureTLS}
                    onChange={(e) => setSettings((prev) => ({ ...prev, allowInsecureTLS: e.target.checked }))}
                    className="mt-0.5"
                  />
                  <span>
                    Allow an untrusted TLS certificate
                    <span className="block text-xs text-gray-500 mt-0.5">Leave off. Only tick this if your mail server uses a self-signed certificate — it disables certificate checking for this organization and should be avoided.</span>
                  </span>
                </label>
              </div>


            </div>

            {/* Inbound Email (IMAP) — uses the credentials above. */}
            <div className="mt-10 pt-6 border-t border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Inbound Email (IMAP)</h2>
              <p className="text-sm text-gray-600 mt-1">
                Pull replies from your mailbox into the CRM. Uses the same email account configured above (we don't ask for the credentials twice). You only need the IMAP host — port 993 is implied.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">IMAP Host</label>
                  <input
                    type="text"
                    name="imap.host"
                    value={settings.imap?.host || ''}
                    onChange={handleInputChange}
                    placeholder="imap.gmail.com / imappro.zoho.com / outlook.office365.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">IMAP Port</label>
                  <input
                    type="number"
                    name="imap.port"
                    value={settings.imap?.port || 993}
                    onChange={handleInputChange}
                    placeholder="993"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="imap.autoCreateLeads"
                      checked={settings.imap?.autoCreateLeads !== false}
                      onChange={(e) => setSettings((p) => ({ ...p, imap: { ...(p.imap || {}), autoCreateLeads: e.target.checked } }))}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      <b>Auto-create leads from new senders</b> <span className="text-xs text-gray-500">(on by default)</span>
                      <span className="block text-xs text-gray-500 mt-0.5">Every email from an unknown sender becomes a fresh lead. Spam filtering happens at your provider — what reaches INBOX is treated as real prospect traffic. Untick when connecting a mailbox that gets a lot of newsletter / system / internal traffic.</span>
                    </span>
                  </label>
                </div>
              </div>
              {settings.imap?.lastSyncAt && (
                <p className="text-xs text-gray-500 mt-3">
                  Last poll: {new Date(settings.imap.lastSyncAt).toLocaleString()}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                The poller runs every 5 minutes. To trigger an immediate poll for testing, use <b>Inbound Channels → Email inbox → Poll now</b>.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>

              <button
                type="button"
                onClick={handleTest}
                disabled={testing || !settings.host || !settings.auth.user || !settings.auth.pass || !settings.from}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                {testing ? 'Testing...' : 'Test SMTP'}
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Reset Settings
              </button>
            </div>
          </form>

          {/* Help Section */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Help</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p><strong>For Gmail:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Host: smtp.gmail.com</li>
                <li>Port: 587 (TLS - automatic)</li>
                <li>You may need to use App Password</li>
              </ul>
              
              <p className="mt-4"><strong>For Outlook/Hotmail:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Host: smtp-mail.outlook.com</li>
                <li>Port: 587 (TLS - automatic)</li>
              </ul>

              <p className="mt-4"><strong>For Yahoo:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Host: smtp.mail.yahoo.com</li>
                <li>Port: 587 (TLS - automatic)</li>
                <li>App Password required</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmtpSettings; 